// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Retours utilisateurs — annotations sur une analyse, suggestions produit et
// évaluations (notes). Couvre le panneau côté client et les pages Admin
// correspondantes.
//
// ⚠️ Deux jeux de libellés de statut de suggestion coexistent et ne sont PAS
// interchangeables : `sugg_status_*` (page AdminRatings) et `sugg_panel_*`
// (panneau d'annotation). Même chose pour les catégories : `sugg_cat_*` vs
// `sugg_panel_cat_*`. Le doublon est antérieur à la découpe et volontairement
// conservé — les fusionner est une décision produit, pas un nettoyage.

const annotations = {
  fr: {
    // ── Panneau ──
    panel_title:              '📝 Annotations',
    panel_tab_annotations:    '📌 Annotations',
    panel_tab_suggestions:    '💡 Suggestions',
    panel_close:              'Fermer',
    panel_save:               'Enregistrer',
    panel_admin_reply:        'Réponse admin',
    panel_reply_placeholder:  "Réponse visible par l'utilisateur...",

    // ── Annotations : saisie ──
    ann_type_label:           'Type',
    ann_target_label:         'Cible',
    ann_content_ph:           'Décrivez le problème constaté...',
    ann_content_required:     'Le contenu est requis',
    ann_content_required_msg: 'Le contenu est requis',
    ann_modified:             '· modifié',
    ann_delete_confirm:       'Supprimer cette annotation ?',
    ann_loading:              '⏳ Chargement...',
    ann_empty:                'Aucune annotation sur cette analyse',

    // ── Annotations : types et cibles ──
    ann_type_parsing:         'Donnée mal lue',
    ann_type_display:         "Problème d'affichage",
    ann_type_error:           'Erreur de résultat',
    ann_type_anomaly:         'Anomalie détectée',
    ann_type_other:           'Autre commentaire',
    ann_target_json1:         'Extraction brute',
    ann_target_json2:         'Données finales',
    ann_target_html:          'Rapport HTML',
    ann_target_pdf:           'PDF source',
    ann_target_general:       'Général',

    // ── Annotations : aide à la saisie ──
    ann_tip_parsing:          "Une information n'a pas été correctement extraite (ex : chiffre, date, nom de pièce)",
    ann_tip_display:          "L'info est correcte mais mal présentée dans le rapport (ex : mise en page, tableau)",
    ann_tip_error:            "Le résultat final est incorrect ou incohérent (ex : pièce classée positive à tort)",
    ann_tip_anomaly:          "Quelque chose semble suspect sans être forcément une erreur",
    ann_tip_other:            "Tout commentaire hors catégories précédentes",

    // ── Annotations : statuts (Admin) ──
    ann_status_label:         'Statut annotation',
    ann_status_open:          'Ouvert',
    ann_status_processing:    'En traitement',
    ann_status_rejected:      'Rejeté',
    ann_status_closed:        'Clôturé',
    resolution_label:         'Statut résolution',
    resolution_resolved:      'Résolu',
    resolution_unresolved:    'Non résolu',
    resolution_in_progress:   'En cours',
    resolution_unknown:       'Inconnu',

    // ── Suggestions : saisie ──
    sugg_category_label:      'Catégorie',
    sugg_content_ph:          'Décrivez votre suggestion...',
    sugg_content_required:    'Le contenu est requis',

    // ── Suggestions : catégories et statuts (Admin) ──
    sugg_cat_extraction:      'Extraction',
    sugg_cat_display:         'Affichage',
    sugg_cat_feature:         'Nouvelle fonctionnalité',
    sugg_cat_bug:             'Bug',
    sugg_cat_other:           'Autre',
    sugg_status_open:         'Ouvert',
    sugg_status_in_progress:  'En cours',
    sugg_status_done:         'Résolu',
    sugg_status_rejected:     'Rejeté',

    // ── Suggestions : catégories et statuts (panneau) ──
    sugg_panel_cat_ui:        'Interface',
    sugg_panel_cat_parsing:   'Extraction de données',
    sugg_panel_cat_rules:     'Règles métier',
    sugg_panel_cat_output:    'Rapport généré',
    sugg_panel_cat_other:     'Autre',
    sugg_panel_open:          'Ouvert',
    sugg_panel_in_progress:   'En cours',
    sugg_panel_done:          'Résolu',
    sugg_panel_rejected:      'Rejeté',

    // ── Évaluations ──
    rating_title:             'Évaluer cette analyse :',
    rating_thanks:            '✅ Merci pour votre évaluation !',
    rating_comment_ph:        'Commentaire optionnel...',
    rating_submit:            '✅ Envoyer',
    distrib_title:            'Distribution des notes',
    distrib_remove_filter:    'Supprimer le filtre',
    distrib_stars:            'étoiles',

    // Origine d'une annotation (colonne « Src » de AdminAnnotations)
    src_ia:                   '🤖 IA',
    src_human:                '👤 Humain',

    // ── Boucle de retour « cet avis est faux » (S3, 25/08/2026) ──
    //
    // ⚠️ TROIS POSITIONS, TROIS MOTS : Favorable / Réserve / Défavorable.
    // ⛔ Jamais « conforme », jamais « favorable avec réserve », jamais
    // « partiellement favorable ». Ces libellés sortent de la doctrine des
    // 13-15/08/2026 et se retrouvent tels quels dans la vérité terrain : les
    // reformuler ici ferait diverger ce que la personne lit de ce qu'on mesure.
    retour_btn:               '🚩 Signaler un avis',
    retour_title:             '🚩 Un avis vous semble faux ?',
    // Dire d'emblée que le rapport ne bouge pas : sans cela, la personne croit
    // corriger son livrable et découvre l'inverse — le public cible attend du
    // « clé en main » et ne devinera pas qu'il alimente une mesure.
    retour_intro:             "Signalez-nous les avis qui vous paraissent erronés. Votre rapport n'est pas modifié : vos retours servent à corriger Normascan.",
    retour_loading:           '⏳ Chargement...',
    retour_empty:             'Aucune réserve ni avis défavorable sur cette analyse.',
    retour_empty_hint:        "Vous pouvez tout de même nous signaler un écart que Normascan n'aurait pas vu.",
    retour_close:             'Fermer',
    retour_rendered_label:    'Avis rendu',
    retour_flag:              'Cet avis est faux',
    retour_cancel:            'Annuler',
    retour_send:              'Envoyer',
    retour_sending:           '⏳ Envoi...',

    // ── Quelle position aurait été la bonne ──
    retour_expected_label:    'Quel avis aurait été le bon ?',
    retour_pos_favorable:     'Favorable',
    retour_pos_reserve:       'Réserve',
    retour_pos_defavorable:   'Défavorable',
    retour_pos_unknown:       'Je ne sais pas',
    // Le critère qui sépare Réserve de Défavorable, dit en une phrase : c'est la
    // distinction que le produit peine le plus à tenir, et celle sur laquelle un
    // retour mal orienté coûte le plus cher à la mesure.
    retour_tip_favorable:     'Rien à redire sur ce point.',
    retour_tip_reserve:       "Il y a un doute : ce qui trancherait se trouve ailleurs (annexe, autre document).",
    retour_tip_defavorable:   "L'écart est bien réel.",
    retour_tip_unknown:       'Je ne peux pas trancher.',

    // ── Le pourquoi, obligatoire ──
    retour_why_label:         'Pourquoi ?',
    retour_why_ph:            "Ex : le PV du laboratoire figure bien en annexe 4, page 23.",
    retour_why_help:          "Sans cette explication, votre signalement ne sera pas exploitable dans six mois.",
    retour_char_min:          "caractères minimum",
    retour_need_position:     "Choisissez d'abord l'avis qui aurait dû être rendu.",
    retour_why_required:      "Expliquez en quelques mots ce qui ne va pas.",
    retour_sent:              '✅ Merci, votre signalement est enregistré.',
    retour_flagged:           '🚩 Signalé',
    retour_edit:              'Modifier',
    retour_delete:            'Retirer',
    retour_delete_confirm:    'Retirer ce signalement ?',
    retour_error:             'Le signalement n\'a pas pu être envoyé.',

    // ── Écart non vu (alimente la mesure du rappel) ──
    retour_missing_title:     "Un écart que Normascan n'a pas vu ?",
    retour_missing_hint:      "Décrivez ce qui aurait dû être relevé et ne l'a pas été.",
    retour_missing_ph:        'Ex : aucune date de prélèvement dans le tableau page 12.',
    retour_missing_send:      'Signaler cet écart',
    retour_missing_count:     'écart(s) non vu(s) signalé(s)',

    // ── Écran d'administration ──
    retour_admin_title:       '🚩 Signalements d\'avis',
    retour_admin_subtitle:    'Ce que les utilisateurs contestent — la vérité terrain de S0',
    retour_admin_to_sort:     'à trier',
    retour_admin_filter:      'Traitement',
    retour_admin_filter_all:  'Tous',
    retour_admin_kind:        'Nature',
    retour_admin_kind_all:    'Toutes',
    retour_admin_kind_position: 'Avis contesté',
    retour_admin_kind_missing:  'Écart non vu',
    retour_status_nouveau:    'À trier',
    retour_status_retenu:     'Retenu',
    retour_status_ecarte:     'Écarté',
    retour_admin_col_date:    'Date',
    retour_admin_col_user:    'Utilisateur',
    retour_admin_col_report:  'Rapport',
    retour_admin_col_rule:    'Règle',
    retour_admin_col_rendered: 'Rendu',
    retour_admin_col_expected: 'Aurait dû être',
    retour_admin_col_why:     'Pourquoi',
    retour_admin_col_status:  'Traitement',
    retour_admin_keep:        'Retenir',
    retour_admin_dismiss:     'Écarter',
    retour_admin_note_ph:     'Note interne (optionnelle)...',
    retour_admin_by_rule:     'Règles les plus contestées',
    retour_admin_by_rule_total: 'signalements',
    retour_admin_by_rule_kept:  'retenus',
    retour_admin_empty:       'Aucun signalement pour le moment.',
    retour_admin_export:      'Exporter pour S0',
    retour_admin_export_precision: 's0_precision.csv',
    retour_admin_export_rappel:    's0_rappel.csv',
    // Le mode d'emploi tient en une ligne, et il évite d'écrire un second noteur.
    retour_admin_export_hint: 'Déposez les deux fichiers dans un dossier, puis : python scripts/noter_s0.py --dossier <dossier>',
    retour_admin_export_warn: 'Seuls les signalements RETENUS sont exportés : un signalement non trié est une réclamation, pas une mesure.',
    retour_admin_export_empty: "Aucun signalement retenu pour l'instant : l'export produirait un fichier vide. Triez d'abord les signalements ci-dessous en « Retenu ».",
  },

  en: {
    // ── Panel ──
    panel_title:              '📝 Annotations',
    panel_tab_annotations:    '📌 Annotations',
    panel_tab_suggestions:    '💡 Suggestions',
    panel_close:              'Close',
    panel_save:               'Save',
    panel_admin_reply:        'Admin reply',
    panel_reply_placeholder:  'Reply visible to the user...',

    // ── Annotations: input ──
    ann_type_label:           'Type',
    ann_target_label:         'Target',
    ann_content_ph:           'Describe the issue observed...',
    ann_content_required:     'Content is required',
    ann_content_required_msg: 'Content is required',
    ann_modified:             '· edited',
    ann_delete_confirm:       'Delete this annotation?',
    ann_loading:              '⏳ Loading...',
    ann_empty:                'No annotations for this analysis',

    // ── Annotations: types and targets ──
    ann_type_parsing:         'Misread data',
    ann_type_display:         'Display issue',
    ann_type_error:           'Result error',
    ann_type_anomaly:         'Anomaly detected',
    ann_type_other:           'Other comment',
    ann_target_json1:         'Raw extraction',
    ann_target_json2:         'Final data',
    ann_target_html:          'HTML report',
    ann_target_pdf:           'Source PDF',
    ann_target_general:       'General',

    // ── Annotations: input tips ──
    ann_tip_parsing:          "Information was not correctly extracted (e.g. number, date, room name)",
    ann_tip_display:          "Info is correct but poorly presented in the report (e.g. layout, table)",
    ann_tip_error:            "Final result is incorrect or inconsistent (e.g. room wrongly flagged positive)",
    ann_tip_anomaly:          "Something seems suspicious without necessarily being an error",
    ann_tip_other:            "Any comment outside the above categories",

    // ── Annotations: statuses (Admin) ──
    ann_status_label:         'Annotation status',
    ann_status_open:          'Open',
    ann_status_processing:    'Processing',
    ann_status_rejected:      'Rejected',
    ann_status_closed:        'Closed',
    resolution_label:         'Resolution status',
    resolution_resolved:      'Resolved',
    resolution_unresolved:    'Unresolved',
    resolution_in_progress:   'In progress',
    resolution_unknown:       'Unknown',

    // ── Suggestions: input ──
    sugg_category_label:      'Category',
    sugg_content_ph:          'Describe your suggestion...',
    sugg_content_required:    'Content is required',

    // ── Suggestions: categories and statuses (Admin) ──
    sugg_cat_extraction:      'Extraction',
    sugg_cat_display:         'Display',
    sugg_cat_feature:         'New feature',
    sugg_cat_bug:             'Bug',
    sugg_cat_other:           'Other',
    sugg_status_open:         'Open',
    sugg_status_in_progress:  'In progress',
    sugg_status_done:         'Resolved',
    sugg_status_rejected:     'Rejected',

    // ── Suggestions: categories and statuses (panel) ──
    sugg_panel_cat_ui:        'Interface',
    sugg_panel_cat_parsing:   'Data extraction',
    sugg_panel_cat_rules:     'Business rules',
    sugg_panel_cat_output:    'Generated report',
    sugg_panel_cat_other:     'Other',
    sugg_panel_open:          'Open',
    sugg_panel_in_progress:   'In progress',
    sugg_panel_done:          'Resolved',
    sugg_panel_rejected:      'Rejected',

    // ── Ratings ──
    rating_title:             'Rate this analysis:',
    rating_thanks:            '✅ Thank you for your rating!',
    rating_comment_ph:        'Optional comment...',
    rating_submit:            '✅ Submit',
    distrib_title:            'Rating distribution',
    distrib_remove_filter:    'Remove filter',
    distrib_stars:            'stars',

    // Annotation origin (« Src » column in AdminAnnotations)
    src_ia:                   '🤖 AI',
    src_human:                '👤 Human',

    // ── "This opinion is wrong" feedback loop (S3, 25/08/2026) ──
    //
    // ⚠️ THREE POSITIONS, THREE WORDS: Favourable / Reservation / Unfavourable.
    // Aligned on `favorable_label` / `avec_reserve_label` / `defavorable_label`
    // in `commun.js`. ⛔ Never "compliant", never "favourable with reservation".
    retour_btn:               '🚩 Report an opinion',
    retour_title:             '🚩 Does an opinion look wrong?',
    retour_intro:             'Report any opinion that looks wrong to you. Your report is not modified: your feedback is used to improve Normascan.',
    retour_loading:           '⏳ Loading...',
    retour_empty:             'No reservation or unfavourable opinion on this analysis.',
    retour_empty_hint:        'You can still report a discrepancy Normascan may have missed.',
    retour_close:             'Close',
    retour_rendered_label:    'Opinion given',
    retour_flag:              'This opinion is wrong',
    retour_cancel:            'Cancel',
    retour_send:              'Send',
    retour_sending:           '⏳ Sending...',

    // ── Which position would have been right ──
    retour_expected_label:    'Which opinion would have been right?',
    retour_pos_favorable:     'Favourable',
    retour_pos_reserve:       'Reservation',
    retour_pos_defavorable:   'Unfavourable',
    retour_pos_unknown:       "I don't know",
    retour_tip_favorable:     'Nothing wrong on this point.',
    retour_tip_reserve:       'There is a doubt: what would settle it is elsewhere (appendix, another document).',
    retour_tip_defavorable:   'The discrepancy is real.',
    retour_tip_unknown:       'I cannot decide.',

    // ── The why, mandatory ──
    retour_why_label:         'Why?',
    retour_why_ph:            'E.g. the lab report is in appendix 4, page 23.',
    retour_why_help:          'Without this explanation, your report will not be usable six months from now.',
    retour_char_min:          'characters minimum',
    retour_need_position:     'Please choose which position should have been given.',
    retour_why_required:      'Please explain briefly what is wrong.',
    retour_sent:              '✅ Thank you, your report has been recorded.',
    retour_flagged:           '🚩 Reported',
    retour_edit:              'Edit',
    retour_delete:            'Remove',
    retour_delete_confirm:    'Remove this report?',
    retour_error:             'The report could not be sent.',

    // ── Missed discrepancy (feeds the recall measurement) ──
    retour_missing_title:     'A discrepancy Normascan missed?',
    retour_missing_hint:      'Describe what should have been flagged and was not.',
    retour_missing_ph:        'E.g. no sampling date in the table on page 12.',
    retour_missing_send:      'Report this discrepancy',
    retour_missing_count:     'missed discrepancy/ies reported',

    // ── Admin screen ──
    retour_admin_title:       '🚩 Opinion reports',
    retour_admin_subtitle:    'What users dispute — the S0 ground truth',
    retour_admin_to_sort:     'to sort',
    retour_admin_filter:      'Handling',
    retour_admin_filter_all:  'All',
    retour_admin_kind:        'Kind',
    retour_admin_kind_all:    'All',
    retour_admin_kind_position: 'Disputed opinion',
    retour_admin_kind_missing:  'Missed discrepancy',
    retour_status_nouveau:    'To sort',
    retour_status_retenu:     'Kept',
    retour_status_ecarte:     'Dismissed',
    retour_admin_col_date:    'Date',
    retour_admin_col_user:    'User',
    retour_admin_col_report:  'Report',
    retour_admin_col_rule:    'Rule',
    retour_admin_col_rendered: 'Given',
    retour_admin_col_expected: 'Should have been',
    retour_admin_col_why:     'Why',
    retour_admin_col_status:  'Handling',
    retour_admin_keep:        'Keep',
    retour_admin_dismiss:     'Dismiss',
    retour_admin_note_ph:     'Internal note (optional)...',
    retour_admin_by_rule:     'Most disputed rules',
    retour_admin_by_rule_total: 'reports',
    retour_admin_by_rule_kept:  'kept',
    retour_admin_empty:       'No reports yet.',
    retour_admin_export:      'Export for S0',
    retour_admin_export_precision: 's0_precision.csv',
    retour_admin_export_rappel:    's0_rappel.csv',
    retour_admin_export_hint: 'Drop both files into one folder, then: python scripts/noter_s0.py --dossier <folder>',
    retour_admin_export_warn: 'Only KEPT reports are exported: an unsorted report is a complaint, not a measurement.',
    retour_admin_export_empty: 'No report has been kept yet: the export would produce an empty file. Sort the reports below as “Kept” first.',
  },
};

export default annotations;
